import { defaultColors } from '@assets/colors/defaultColors';
import { addRecentColor, getRecentColors } from '@src/utils/recentColorsUtil';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FaPlus } from 'react-icons/fa';

type ColorPickerProps = {
  recentColor: string;
  onColorSelect: (color: string) => void;
  onClose: () => void;
  style?: React.CSSProperties;
};

export function ColorPicker({
  recentColor,
  onColorSelect,
  onClose,
  style,
}: ColorPickerProps): JSX.Element {
  const [recentColors, setRecentColors] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const updateRecentColors = () => setRecentColors(getRecentColors());

  const handleColorConfirm = useCallback(
    (e: Event) => {
      const color = (e.target as HTMLInputElement).value;
      onColorSelect(color);
      addRecentColor(color);
      onClose();
    },
    [onColorSelect, onClose],
  );

  useEffect(() => {
    updateRecentColors();

    // StorageEventを監視してlocalStorageの変更を検知
    window.addEventListener('storage', updateRecentColors);

    const input = inputRef.current;
    if (!input) {
      return () => window.removeEventListener('storage', updateRecentColors);
    }

    // ReactのonChangeは色選択中でも発火してしまうため、addEventListenerを使用する
    input.addEventListener('change', handleColorConfirm);

    return () => {
      window.removeEventListener('storage', updateRecentColors);
      input.removeEventListener('change', handleColorConfirm);
    };
  }, [handleColorConfirm]);

  return (
    <div
      className='dropdownMenu'
      style={style}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className='colorGrid'>
        {defaultColors.map((color, idx) => (
          <div
            key={idx}
            className='colorSwatch'
            style={{ backgroundColor: color }}
            onClick={() => {
              onColorSelect(color);
              onClose();
            }}
          />
        ))}
      </div>
      {recentColors.length > 0 && (
        <>
          <div className='pb-1 text-xs text-gray-500'>最近使用した色</div>
          <div className='colorGrid'>
            {recentColors.map((color, idx) => (
              <div
                key={`recent-${idx}`}
                className='colorSwatch'
                style={{ backgroundColor: color }}
                onClick={() => {
                  onColorSelect(color);
                  onClose();
                }}
              />
            ))}
          </div>
        </>
      )}
      <div className='flexEnd'>
        <div
          className='colorSwatch'
          style={{ backgroundColor: recentColor }}
          onClick={() => {
            onColorSelect(recentColor);
            onClose();
          }}
        />
        <div className='colorButton'>
          <input
            type='color'
            ref={inputRef}
            className='hidden-color-input'
          />
          <div className='color-picker-button'>
            <FaPlus />
          </div>
        </div>
      </div>
    </div>
  );
}
